import React, { useState, useEffect, useRef } from "react";

// 根据温度和状态计算最大速度，确保动能与温度对应
const calculateMaxSpeed = (temp, state) => {
  const kelvinEquivalent = Math.max(temp + 273.15, 50);
  const baseSpeed = Math.sqrt(kelvinEquivalent / 100);

  if (state === "solid") {
    return Math.max(baseSpeed * 0.3, 0.2);
  } else if (state === "liquid") {
    return Math.max(baseSpeed * 0.8, 1.0);
  } else {
    return Math.max(baseSpeed * 1.5, 2.0);
  }
};

// 根据温度和状态计算热噪声，确保振动与温度对应
const calculateThermalNoise = (temp, state) => {
  const kelvinEquivalent = Math.max(temp + 273.15, 50);
  const baseNoise = kelvinEquivalent / 500;

  if (state === "solid") {
    return Math.max(baseNoise * 2.0, 0.15);
  } else if (state === "liquid") {
    return baseNoise * 1.5;
  } else {
    return baseNoise * 3.0;
  }
};

// 根据温度计算热分子速度（基于分子动理论）
const calculateThermalMoleculeSpeed = (temp) => {
  const kelvin = temp + 273.15;
  // v ∝ √T，使用更科学的速度计算
  return Math.sqrt(kelvin / 273.15) * 5.0; // 基准速度为5.0
};

const MolecularStatesSimulation = () => {
  const canvasRef = useRef(null);
  const animationRef = useRef(null);

  // 分离的温度控制 - 改为固态开始
  const [objectTemperature, setObjectTemperature] = useState(-15);
  const [targetTemperature, setTargetTemperature] = useState(-15);
  const [autoHeating, setAutoHeating] = useState(false);
  const [autoCooling, setAutoCooling] = useState(false);

  // 物态变化状态
  const [phaseTransition, setPhaseTransition] = useState(null);
  const [phaseProgress, setPhaseProgress] = useState(0);

  const [currentState, setCurrentState] = useState("固体");
  const [showForces, setShowForces] = useState(false);
  const [showShape, setShowShape] = useState(true);

  // 使用refs来存储最新的状态值，避免useEffect重启
  const objectTemperatureRef = useRef(-15);
  const targetTemperatureRef = useRef(-15);
  const autoHeatingRef = useRef(false);
  const autoCoolingRef = useRef(false);
  const phaseTransitionRef = useRef(null);
  const phaseProgressRef = useRef(0);
  const showForcesRef = useRef(false);
  const showShapeRef = useRef(true);

  // 边界平滑用的历史记录
  const shapeHistoryRef = useRef([]);
  const SHAPE_HISTORY_SIZE = 10;

  // 同步state到ref
  useEffect(() => {
    objectTemperatureRef.current = objectTemperature;
  }, [objectTemperature]);
  useEffect(() => {
    targetTemperatureRef.current = targetTemperature;
  }, [targetTemperature]);
  useEffect(() => {
    autoHeatingRef.current = autoHeating;
  }, [autoHeating]);
  useEffect(() => {
    autoCoolingRef.current = autoCooling;
  }, [autoCooling]);
  useEffect(() => {
    phaseTransitionRef.current = phaseTransition;
  }, [phaseTransition]);
  useEffect(() => {
    phaseProgressRef.current = phaseProgress;
  }, [phaseProgress]);
  useEffect(() => {
    showForcesRef.current = showForces;
  }, [showForces]);
  useEffect(() => {
    showShapeRef.current = showShape;
  }, [showShape]);

  // 物理常数
  const CANVAS_WIDTH = 800;
  const CANVAS_HEIGHT = 600;
  const MOLECULE_COUNT = 48;
  const THERMAL_MOLECULE_COUNT = 25; // 增加热/冷分子数量
  const ATTRACTION_STRENGTH = 0.5;

  // 固定的热/冷分子温度
  const HOT_MOLECULE_TEMP = 500;
  const COLD_MOLECULE_TEMP = -50;
  const TEMPERATURE_CHANGE_RATE = 0.3; // 增加温度变化速率
  const PHASE_TRANSITION_RATE = 0.0558;

  // 物态变化温度点
  const MELTING_POINT = 0;
  const BOILING_POINT = 100;

  // 分子类
  class Molecule {
    constructor(x, y, isThermal = false, isCold = false) {
      this.x = x;
      this.y = y;
      this.equilibriumX = x;
      this.equilibriumY = y;
      this.vx = (Math.random() - 0.5) * 2;
      this.vy = (Math.random() - 0.5) * 2;
      this.fx = 0;
      this.fy = 0;
      this.radius = isThermal ? 3 : 6;
      this.mass = isThermal ? 0.5 : 1;
      this.isThermal = isThermal;
      this.isCold = isCold;
      this.energy = 0;
      this.forces = [];
      this.collisionCooldown = 0;
      this.phaseState = "solid";
    }

    calculateForces(molecules, temp, phaseTransition, phaseProgress) {
      this.fx = 0;
      this.fy = 0;
      this.forces = [];

      if (this.isThermal) {
        return;
      }

      const effectiveState = this.determineEffectiveState(
        temp,
        phaseTransition,
        phaseProgress
      );

      let attractionStrength = ATTRACTION_STRENGTH;
      let equilibriumDistance = 40;
      let restoreForce = 0;

      if (effectiveState === "solid") {
        attractionStrength = ATTRACTION_STRENGTH * 3;
        equilibriumDistance = 35;
        const dx_eq = this.equilibriumX - this.x;
        const dy_eq = this.equilibriumY - this.y;

        let restoreForceMultiplier = 1;
        if (phaseTransition === "melting" && phaseProgress < 70) {
          restoreForceMultiplier = 2.0;
        }

        restoreForce = 0.1 * restoreForceMultiplier;
        this.fx += dx_eq * restoreForce;
        this.fy += dy_eq * restoreForce;

        // 增强固态振动的可见性，确保温度越高振动越明显
        const thermalVibration = calculateThermalNoise(temp, "solid");
        this.fx += (Math.random() - 0.5) * thermalVibration;
        this.fy += (Math.random() - 0.5) * thermalVibration;
      } else if (effectiveState === "liquid") {
        attractionStrength = ATTRACTION_STRENGTH * 3.5; // 大幅增强液体分子间吸引力
        equilibriumDistance = 40; // 减小液体分子间距
      } else {
        attractionStrength = ATTRACTION_STRENGTH * 0.2;
        equilibriumDistance = 100;
      }

      molecules.forEach((other) => {
        if (other === this || other.isThermal) return;

        const dx = other.x - this.x;
        const dy = other.y - this.y;
        const distance = Math.sqrt(dx * dx + dy * dy);

        if (distance > 0) {
          let forceX = 0,
            forceY = 0;

          if (distance < equilibriumDistance) {
            const repulsionForce =
              (attractionStrength * 2) / (distance * distance);
            forceX = (-repulsionForce * dx) / distance;
            forceY = (-repulsionForce * dy) / distance;
            this.fx += forceX;
            this.fy += forceY;

            this.forces.push({
              targetX: other.x,
              targetY: other.y,
              forceX: forceX * 10,
              forceY: forceY * 10,
              magnitude: repulsionForce * 10,
              type: "repulsion",
            });
          } else if (distance < equilibriumDistance * 2.5) {
            const attractionForce =
              (attractionStrength / (distance * distance)) * this.mass;
            forceX = (attractionForce * dx) / distance;
            forceY = (attractionForce * dy) / distance;
            this.fx += forceX;
            this.fy += forceY;

            this.forces.push({
              targetX: other.x,
              targetY: other.y,
              forceX: forceX * 10,
              forceY: forceY * 10,
              magnitude: attractionForce * 10,
              type: "attraction",
            });
          }

          // 液体表面张力：液滴间的长距离弱吸引力
          if (
            effectiveState === "liquid" &&
            distance > equilibriumDistance * 2.5 &&
            distance < 120
          ) {
            const surfaceTension =
              (ATTRACTION_STRENGTH * 0.08) / (distance * distance);
            const tensionForceX = (surfaceTension * dx) / distance;
            const tensionForceY = (surfaceTension * dy) / distance;
            this.fx += tensionForceX;
            this.fy += tensionForceY;

            this.forces.push({
              targetX: other.x,
              targetY: other.y,
              forceX: tensionForceX * 15,
              forceY: tensionForceY * 15,
              magnitude: surfaceTension * 15,
              type: "surface_tension",
            });
          }
        }
      });
    }

    determineEffectiveState(temp, phaseTransition, phaseProgress) {
      if (!phaseTransition) {
        if (temp < MELTING_POINT) return "solid";
        if (temp < BOILING_POINT) return "liquid";
        return "gas";
      }

      const moleculeIndex = (this.x * 0.1 + this.y * 0.1) % 1000;
      const progressThreshold = (moleculeIndex % 1000) / 1000;

      let effectiveProgress = phaseProgress / 100;

      if (phaseTransition === "melting" || phaseTransition === "freezing") {
        if (effectiveProgress < 0.6) {
          effectiveProgress = effectiveProgress * 0.3;
        } else {
          effectiveProgress = 0.18 + (effectiveProgress - 0.6) * 2.05;
        }
      }

      if (
        phaseTransition === "vaporizing" ||
        phaseTransition === "condensing"
      ) {
        if (effectiveProgress < 0.4) {
          effectiveProgress = effectiveProgress * 0.2;
        } else {
          effectiveProgress = 0.08 + (effectiveProgress - 0.4) * 1.53;
        }
      }

      const smoothProgress =
        effectiveProgress + Math.sin(Date.now() * 0.001 + moleculeIndex) * 0.03;

      switch (phaseTransition) {
        case "melting":
          return smoothProgress > progressThreshold ? "liquid" : "solid";
        case "freezing":
          return smoothProgress > progressThreshold ? "solid" : "liquid";
        case "vaporizing":
          return smoothProgress > progressThreshold ? "gas" : "liquid";
        case "condensing":
          return smoothProgress > progressThreshold ? "liquid" : "gas";
        default:
          if (temp < MELTING_POINT) return "solid";
          if (temp < BOILING_POINT) return "liquid";
          return "gas";
      }
    }

    update(temp, phaseTransition, phaseProgress) {
      if (this.collisionCooldown > 0) {
        this.collisionCooldown--;
      }

      const effectiveState = this.determineEffectiveState(
        temp,
        phaseTransition,
        phaseProgress
      );
      const previousState = this.phaseState || effectiveState;
      this.phaseState = effectiveState;

      if (
        phaseTransition === "vaporizing" &&
        previousState === "liquid" &&
        effectiveState === "gas"
      ) {
        const escapeAngle = Math.random() * Math.PI * 2;
        const escapeSpeed = 3.0 + Math.random() * 2.0;
        this.vx += Math.cos(escapeAngle) * escapeSpeed;
        this.vy += Math.sin(escapeAngle) * escapeSpeed;
      }

      if (this.isThermal) {
        if (this.isCold) {
          // 冷分子速度基于温度计算
          const coldSpeed = calculateThermalMoleculeSpeed(COLD_MOLECULE_TEMP);
          const directionNoise = 0.05;
          this.vx += (Math.random() - 0.5) * directionNoise;
          this.vy += (Math.random() - 0.5) * directionNoise;
          const speed = Math.sqrt(this.vx * this.vx + this.vy * this.vy);
          if (speed > coldSpeed) {
            this.vx = (this.vx / speed) * coldSpeed;
            this.vy = (this.vy / speed) * coldSpeed;
          }
        } else {
          // 热分子速度基于温度计算
          const hotSpeed = calculateThermalMoleculeSpeed(HOT_MOLECULE_TEMP);
          const directionNoise = 0.15;
          this.vx += (Math.random() - 0.5) * directionNoise;
          this.vy += (Math.random() - 0.5) * directionNoise;
          const speed = Math.sqrt(this.vx * this.vx + this.vy * this.vy);
          if (speed > hotSpeed) {
            this.vx = (this.vx / speed) * hotSpeed;
            this.vy = (this.vy / speed) * hotSpeed;
          }
        }
      } else {
        this.vx += this.fx / this.mass;
        this.vy += this.fy / this.mass;

        // 使用真实的温度-速度关系
        const thermalNoise = calculateThermalNoise(temp, effectiveState);
        const maxSpeed = calculateMaxSpeed(temp, effectiveState);

        let dampingFactor;
        if (effectiveState === "solid") {
          dampingFactor = 0.9; // 固态阻尼最大
        } else if (effectiveState === "liquid") {
          dampingFactor = 0.94; // 液态阻尼适中
        } else {
          dampingFactor = 0.99; // 气态阻尼最小
        }

        this.vx += (Math.random() - 0.5) * thermalNoise;
        this.vy += (Math.random() - 0.5) * thermalNoise;

        const speed = Math.sqrt(this.vx * this.vx + this.vy * this.vy);
        if (speed > maxSpeed) {
          this.vx = (this.vx / speed) * maxSpeed;
          this.vy = (this.vy / speed) * maxSpeed;
        }

        this.vx *= dampingFactor;
        this.vy *= dampingFactor;
      }

      this.x += this.vx;
      this.y += this.vy;

      if (!this.isThermal) {
        const margin = 50;
        if (this.x < margin) {
          this.x = margin;
          this.vx = Math.abs(this.vx) * 0.8;
        }
        if (this.x > CANVAS_WIDTH - margin) {
          this.x = CANVAS_WIDTH - margin;
          this.vx = -Math.abs(this.vx) * 0.8;
        }
        if (this.y < margin) {
          this.y = margin;
          this.vy = Math.abs(this.vy) * 0.8;
        }
        if (this.y > CANVAS_HEIGHT - margin) {
          this.y = CANVAS_HEIGHT - margin;
          this.vy = -Math.abs(this.vy) * 0.8;
        }
      }
    }

    checkThermalCollision(thermalMolecules, onTemperatureChange) {
      if (this.collisionCooldown > 0) return;

      thermalMolecules.forEach((thermal) => {
        const dx = thermal.x - this.x;
        const dy = thermal.y - this.y;
        const distance = Math.sqrt(dx * dx + dy * dy);

        if (distance < this.radius + thermal.radius) {
          if (thermal.isCold) {
            onTemperatureChange(-TEMPERATURE_CHANGE_RATE * 1.5);
            const energyTransfer = 0.3;
            thermal.vx += this.vx * energyTransfer * 0.2;
            thermal.vy += this.vy * energyTransfer * 0.2;
            this.vx *= 1 - energyTransfer;
            this.vy *= 1 - energyTransfer;
          } else {
            onTemperatureChange(TEMPERATURE_CHANGE_RATE);
            const energyTransfer = 0.4;
            this.vx += thermal.vx * energyTransfer;
            this.vy += thermal.vy * energyTransfer;
            thermal.vx *= 0.6;
            thermal.vy *= 0.6;
          }

          const overlap = this.radius + thermal.radius - distance;
          if (distance > 0) {
            const separationX = (dx / distance) * overlap * 0.5;
            const separationY = (dy / distance) * overlap * 0.5;
            this.x -= separationX;
            this.y -= separationY;
            thermal.x += separationX;
            thermal.y += separationY;
          }

          this.collisionCooldown = 20; // 减少冷却时间，增加碰撞频率
        }
      });
    }

    static manageThermalMolecules(thermalMolecules, isHeating, isCooling) {
      const CANVAS_W = 800;
      const CANVAS_H = 600;
      const BOUNDARY = 100;

      for (let i = thermalMolecules.length - 1; i >= 0; i--) {
        const thermal = thermalMolecules[i];
        if (
          thermal.x < -BOUNDARY ||
          thermal.x > CANVAS_W + BOUNDARY ||
          thermal.y < -BOUNDARY ||
          thermal.y > CANVAS_H + BOUNDARY
        ) {
          thermalMolecules.splice(i, 1);
        }
      }

      const targetCount = isHeating || isCooling ? THERMAL_MOLECULE_COUNT : 0;
      while (thermalMolecules.length < targetCount) {
        const newThermal = Molecule.createNewThermalMolecule(isCooling);
        thermalMolecules.push(newThermal);
      }
    }

    static createNewThermalMolecule(isCooling) {
      const CANVAS_W = 800;
      const CANVAS_H = 600;
      let x, y, vx, vy;
      const side = Math.floor(Math.random() * 4);

      if (isCooling) {
        const speed = calculateThermalMoleculeSpeed(COLD_MOLECULE_TEMP);
        switch (side) {
          case 0:
            x = Math.random() * CANVAS_W;
            y = -30;
            vx = (Math.random() - 0.5) * speed * 0.5;
            vy = Math.abs(Math.random() * speed);
            break;
          case 1:
            x = CANVAS_W + 30;
            y = Math.random() * CANVAS_H;
            vx = -Math.abs(Math.random() * speed);
            vy = (Math.random() - 0.5) * speed * 0.5;
            break;
          case 2:
            x = Math.random() * CANVAS_W;
            y = CANVAS_H + 30;
            vx = (Math.random() - 0.5) * speed * 0.5;
            vy = -Math.abs(Math.random() * speed);
            break;
          case 3:
            x = -30;
            y = Math.random() * CANVAS_H;
            vx = Math.abs(Math.random() * speed);
            vy = (Math.random() - 0.5) * speed * 0.5;
            break;
        }
        const coldMolecule = new Molecule(x, y, true, true);
        coldMolecule.vx = vx;
        coldMolecule.vy = vy;
        return coldMolecule;
      } else {
        const speed = calculateThermalMoleculeSpeed(HOT_MOLECULE_TEMP);
        switch (side) {
          case 0:
            x = Math.random() * CANVAS_W;
            y = -30;
            vx = (Math.random() - 0.5) * speed * 0.5;
            vy = Math.abs(Math.random() * speed);
            break;
          case 1:
            x = CANVAS_W + 30;
            y = Math.random() * CANVAS_H;
            vx = -Math.abs(Math.random() * speed);
            vy = (Math.random() - 0.5) * speed * 0.5;
            break;
          case 2:
            x = Math.random() * CANVAS_W;
            y = CANVAS_H + 30;
            vx = (Math.random() - 0.5) * speed * 0.5;
            vy = -Math.abs(Math.random() * speed);
            break;
          case 3:
            x = -30;
            y = Math.random() * CANVAS_H;
            vx = Math.abs(Math.random() * speed);
            vy = (Math.random() - 0.5) * speed * 0.5;
            break;
        }
        const hotMolecule = new Molecule(x, y, true, false);
        hotMolecule.vx = vx;
        hotMolecule.vy = vy;
        return hotMolecule;
      }
    }
  }

  // 初始化分子
  const initializeMolecules = (temp) => {
    const molecules = [];
    const thermalMolecules = [];

    if (temp < MELTING_POINT) {
      const cols = 8;
      const rows = 6;
      const spacing = 40;
      const startX = CANVAS_WIDTH / 2 - (cols * spacing) / 2;
      const startY = CANVAS_HEIGHT / 2 - (rows * spacing) / 2;

      for (let i = 0; i < rows; i++) {
        for (let j = 0; j < cols; j++) {
          const x = startX + j * spacing + (Math.random() - 0.5) * 3;
          const y = startY + i * spacing + (Math.random() - 0.5) * 3;
          const molecule = new Molecule(x, y);
          molecule.equilibriumX = startX + j * spacing;
          molecule.equilibriumY = startY + i * spacing;
          molecules.push(molecule);
        }
      }
    } else if (temp < BOILING_POINT) {
      const centerX = CANVAS_WIDTH / 2;
      const centerY = CANVAS_HEIGHT / 2;
      const baseRadius = 100;
      const layers = 6;
      const moleculesPerLayer = Math.ceil(MOLECULE_COUNT / layers);

      for (let layer = 0; layer < layers; layer++) {
        const radius = (baseRadius * (layer + 1)) / layers;
        const countInLayer = Math.min(
          moleculesPerLayer,
          MOLECULE_COUNT - layer * moleculesPerLayer
        );

        for (let i = 0; i < countInLayer; i++) {
          const angle = (i / countInLayer) * 2 * Math.PI + Math.random() * 0.5;
          const r = radius * (0.7 + Math.random() * 0.6);
          const x = centerX + r * Math.cos(angle) + (Math.random() - 0.5) * 25;
          const y = centerY + r * Math.sin(angle) + (Math.random() - 0.5) * 25;
          const molecule = new Molecule(x, y);
          molecule.equilibriumX = x;
          molecule.equilibriumY = y;
          molecules.push(molecule);
          if (molecules.length >= MOLECULE_COUNT) break;
        }
        if (molecules.length >= MOLECULE_COUNT) break;
      }
    } else {
      for (let i = 0; i < MOLECULE_COUNT; i++) {
        const x = 80 + Math.random() * (CANVAS_WIDTH - 160);
        const y = 80 + Math.random() * (CANVAS_HEIGHT - 160);
        const molecule = new Molecule(x, y);
        molecule.equilibriumX = x;
        molecule.equilibriumY = y;
        molecule.vx = (Math.random() - 0.5) * 8;
        molecule.vy = (Math.random() - 0.5) * 8;
        molecules.push(molecule);
      }
    }

    return { molecules, thermalMolecules };
  };

  // 液体区域检测（优化版）
  const findLiquidClusters = (molecules, currentState, phaseTransition) => {
    if (currentState === "气体" && !phaseTransition) {
      return [];
    }
    if (
      phaseTransition &&
      phaseTransition !== "vaporizing" &&
      phaseTransition !== "condensing" &&
      phaseTransition !== "melting"
    ) {
      return [];
    }

    const liquidMolecules = molecules.filter((mol) => {
      let neighbors = 0;
      let speedCompatibleNeighbors = 0;
      const clusterDistance = 95; // 增加聚类距离
      const maxSpeedDifference = 5.0;

      const molSpeed = Math.sqrt(mol.vx * mol.vx + mol.vy * mol.vy);

      molecules.forEach((other) => {
        if (other === mol) return;
        const dx = other.x - mol.x;
        const dy = other.y - mol.y;
        const distance = Math.sqrt(dx * dx + dy * dy);

        if (distance < clusterDistance) {
          neighbors++;

          const otherSpeed = Math.sqrt(
            other.vx * other.vx + other.vy * other.vy
          );
          const speedDifference = Math.abs(molSpeed - otherSpeed);

          const molDirection = Math.atan2(mol.vy, mol.vx);
          const otherDirection = Math.atan2(other.vy, other.vx);
          let directionDifference = Math.abs(molDirection - otherDirection);
          if (directionDifference > Math.PI) {
            directionDifference = 2 * Math.PI - directionDifference;
          }

          if (
            speedDifference < maxSpeedDifference &&
            directionDifference < Math.PI / 1.5
          ) {
            speedCompatibleNeighbors++;
          }
        }
      });

      return neighbors >= 2 && speedCompatibleNeighbors >= 1;
    });

    if (liquidMolecules.length < 4) return []; // 增加最小聚类大小

    const clusters = [];
    const visited = new Set();
    const clusterDistance = 110; // 增加聚类距离

    liquidMolecules.forEach((mol) => {
      if (visited.has(mol)) return;

      const cluster = [];
      const queue = [mol];
      visited.add(mol);

      while (queue.length > 0) {
        const current = queue.shift();
        cluster.push(current);

        liquidMolecules.forEach((other) => {
          if (visited.has(other)) return;

          const dx = other.x - current.x;
          const dy = other.y - current.y;
          const distance = Math.sqrt(dx * dx + dy * dy);

          if (distance < clusterDistance) {
            visited.add(other);
            queue.push(other);
          }
        });
      }

      if (cluster.length >= 4) {
        // 增加最小聚类大小
        clusters.push(cluster);
      }
    });

    return clusters;
  };

  const findClusteredMolecules = (molecules, currentState) => {
    if (currentState === "气体") return [];
    const clusterDistance = currentState === "固体" ? 65 : 85; // 增加聚类距离
    const minClusterSize = Math.floor(molecules.length * 0.7); // 增加最小聚类比例

    const moleculeNeighbors = molecules.map((mol) => {
      let neighbors = 0;
      molecules.forEach((other) => {
        if (other === mol) return;
        const dx = other.x - mol.x;
        const dy = other.y - mol.y;
        const distance = Math.sqrt(dx * dx + dy * dy);
        if (distance < clusterDistance) neighbors++;
      });
      return { molecule: mol, neighbors };
    });

    const clusteredMolecules = moleculeNeighbors
      .filter((item) => item.neighbors >= 2)
      .map((item) => item.molecule);

    if (clusteredMolecules.length < minClusterSize) {
      return molecules;
    }
    return clusteredMolecules;
  };

  const calculateConvexHull = (points) => {
    if (points.length < 3) return points;
    let bottomPoint = points[0];
    for (let i = 1; i < points.length; i++) {
      if (
        points[i].y < bottomPoint.y ||
        (points[i].y === bottomPoint.y && points[i].x < bottomPoint.x)
      ) {
        bottomPoint = points[i];
      }
    }

    const sortedPoints = points
      .filter((p) => p !== bottomPoint)
      .sort((a, b) => {
        const angleA = Math.atan2(a.y - bottomPoint.y, a.x - bottomPoint.x);
        const angleB = Math.atan2(b.y - bottomPoint.y, b.x - bottomPoint.x);
        return angleA - angleB;
      });

    const hull = [bottomPoint];
    for (let point of sortedPoints) {
      while (hull.length > 1) {
        const p1 = hull[hull.length - 2];
        const p2 = hull[hull.length - 1];
        const cross =
          (p2.x - p1.x) * (point.y - p1.y) - (p2.y - p1.y) * (point.x - p1.x);
        if (cross > 0) break;
        hull.pop();
      }
      hull.push(point);
    }
    return hull;
  };

  const smoothBoundary = (points, smoothness = 0.3) => {
    if (points.length < 3) return points;
    const smoothed = [];
    for (let i = 0; i < points.length; i++) {
      const prev = points[(i - 1 + points.length) % points.length];
      const curr = points[i];
      const next = points[(i + 1) % points.length];
      smoothed.push({
        x: curr.x * (1 - smoothness) + ((prev.x + next.x) * smoothness) / 2,
        y: curr.y * (1 - smoothness) + ((prev.y + next.y) * smoothness) / 2,
      });
    }
    return smoothed;
  };

  // 时间平滑函数
  const smoothShapeOverTime = (newShape, history, smoothingFactor = 0.7) => {
    if (history.length === 0) return newShape;

    const lastShape = history[history.length - 1];
    if (!lastShape || lastShape.length !== newShape.length) return newShape;

    // 对每个点进行时间平滑
    const smoothedShape = newShape.map((point, i) => {
      const lastPoint = lastShape[i];
      return {
        x: lastPoint.x * smoothingFactor + point.x * (1 - smoothingFactor),
        y: lastPoint.y * smoothingFactor + point.y * (1 - smoothingFactor),
      };
    });

    return smoothedShape;
  };

  const calculateClusterShape = (cluster) => {
    if (cluster.length < 4) return null; // 增加最小点数要求

    const boundaryPoints = [];
    const boundaryDistance = 40; // 增加边界距离

    cluster.forEach((mol) => {
      for (let angle = 0; angle < Math.PI * 2; angle += Math.PI / 6) {
        // 减少边界点数量
        boundaryPoints.push({
          x: mol.x + Math.cos(angle) * boundaryDistance,
          y: mol.y + Math.sin(angle) * boundaryDistance,
        });
      }
    });

    let hull = calculateConvexHull(boundaryPoints);
    hull = smoothBoundary(hull, 0.5); // 增加平滑度
    hull = smoothBoundary(hull, 0.3);
    return hull;
  };

  const calculateMolecularShape = (
    molecules,
    currentState,
    phaseTransition
  ) => {
    if (currentState === "固体") {
      const clusteredMolecules = findClusteredMolecules(
        molecules,
        currentState
      );
      if (clusteredMolecules.length < 3) return null;

      const xs = clusteredMolecules.map((m) => m.x);
      const ys = clusteredMolecules.map((m) => m.y);
      const minX = Math.min(...xs) - 25;
      const maxX = Math.max(...xs) + 25;
      const minY = Math.min(...ys) - 25;
      const maxY = Math.max(...ys) + 25;

      const cornerRadius = 30;
      const points = [];
      const resolution = 10;

      for (let i = 0; i <= resolution; i++) {
        const angle = -Math.PI / 2 + ((Math.PI / 2) * i) / resolution;
        points.push({
          x: maxX - cornerRadius + cornerRadius * Math.cos(angle),
          y: minY + cornerRadius + cornerRadius * Math.sin(angle),
        });
      }
      for (let i = 0; i <= resolution; i++) {
        const angle = 0 + ((Math.PI / 2) * i) / resolution;
        points.push({
          x: maxX - cornerRadius + cornerRadius * Math.cos(angle),
          y: maxY - cornerRadius + cornerRadius * Math.sin(angle),
        });
      }
      for (let i = 0; i <= resolution; i++) {
        const angle = Math.PI / 2 + ((Math.PI / 2) * i) / resolution;
        points.push({
          x: minX + cornerRadius + cornerRadius * Math.cos(angle),
          y: maxY - cornerRadius + cornerRadius * Math.sin(angle),
        });
      }
      for (let i = 0; i <= resolution; i++) {
        const angle = Math.PI + ((Math.PI / 2) * i) / resolution;
        points.push({
          x: minX + cornerRadius + cornerRadius * Math.cos(angle),
          y: minY + cornerRadius + cornerRadius * Math.sin(angle),
        });
      }

      // 应用时间平滑
      const smoothedPoints = smoothShapeOverTime(
        points,
        shapeHistoryRef.current
      );

      // 更新历史记录
      shapeHistoryRef.current.push(smoothedPoints);
      if (shapeHistoryRef.current.length > SHAPE_HISTORY_SIZE) {
        shapeHistoryRef.current.shift();
      }

      return [smoothedPoints];
    } else {
      const liquidClusters = findLiquidClusters(
        molecules,
        currentState,
        phaseTransition
      );

      if (liquidClusters.length === 0) {
        shapeHistoryRef.current = []; // 清空历史记录
        return null;
      }

      const shapes = liquidClusters
        .map((cluster) => calculateClusterShape(cluster))
        .filter((shape) => shape !== null);

      if (shapes.length === 0) {
        shapeHistoryRef.current = []; // 清空历史记录
        return null;
      }

      // 对每个液体形状应用时间平滑
      const smoothedShapes = shapes.map((shape, index) => {
        const historyForShape = shapeHistoryRef.current[index] || [];
        const smoothedShape = smoothShapeOverTime(shape, historyForShape);
        return smoothedShape;
      });

      // 更新历史记录
      shapeHistoryRef.current = smoothedShapes;

      return smoothedShapes;
    }
  };

  const drawMolecularShapes = (ctx, shapes, currentState) => {
    if (!shapes || shapes.length === 0) return;

    let fillColor, strokeColor;
    if (currentState === "固体") {
      fillColor = "rgba(100, 150, 255, 0.2)";
      strokeColor = "rgba(100, 150, 255, 0.4)";
    } else {
      fillColor = "rgba(120, 200, 255, 0.15)";
      strokeColor = "rgba(120, 200, 255, 0.3)";
    }

    ctx.fillStyle = fillColor;
    ctx.strokeStyle = strokeColor;
    ctx.lineWidth = 2;

    shapes.forEach((shape) => {
      if (!shape || shape.length < 3) return;

      ctx.beginPath();

      if (currentState === "固体") {
        ctx.moveTo(shape[0].x, shape[0].y);
        for (let i = 1; i < shape.length; i++) {
          ctx.lineTo(shape[i].x, shape[i].y);
        }
      } else {
        ctx.moveTo(shape[0].x, shape[0].y);
        for (let i = 0; i < shape.length; i++) {
          const current = shape[i];
          const next = shape[(i + 1) % shape.length];
          const nextNext = shape[(i + 2) % shape.length];
          const cpX = next.x + (nextNext.x - current.x) * 0.1;
          const cpY = next.y + (nextNext.y - current.y) * 0.1;
          ctx.quadraticCurveTo(cpX, cpY, next.x, next.y);
        }
      }
      ctx.closePath();
      ctx.fill();
      ctx.stroke();
    });
  };

  const determineState = (temp) => {
    if (temp < MELTING_POINT) return "固体";
    if (temp < BOILING_POINT) return "液体";
    return "气体";
  };

  const getPhaseTransitionDescription = (transition, progress) => {
    const progressPercent = Math.round(progress);
    switch (transition) {
      case "melting":
        return `正在熔化 ${progressPercent}%`;
      case "freezing":
        return `正在凝固 ${progressPercent}%`;
      case "vaporizing":
        return `正在汽化 ${progressPercent}%`;
      case "condensing":
        return `正在液化 ${progressPercent}%`;
      default:
        return "";
    }
  };

  // 判断是否应该显示分子连接线的函数
  const shouldShowMolecularBonds = (
    newState,
    phaseTransition,
    phaseProgress
  ) => {
    // 如果不在物态变化过程中，按正常状态判断
    if (!phaseTransition) {
      return newState !== "气体";
    }

    // 汽化过程：在70%进度之前仍显示连接线，模拟液体分子逐渐分离的过程
    if (phaseTransition === "vaporizing") {
      return phaseProgress < 70;
    }

    // 液化过程：在30%进度之后开始显示连接线，模拟气体分子逐渐聚集的过程
    if (phaseTransition === "condensing") {
      return phaseProgress > 30;
    }

    // 熔化和凝固过程保持原有逻辑
    if (phaseTransition === "melting" || phaseTransition === "freezing") {
      return true; // 熔化和凝固过程中都显示连接线
    }

    return newState !== "气体";
  };

  const moleculesRef = useRef(null);
  const thermalMoleculesRef = useRef(null);

  // 初始化
  useEffect(() => {
    const { molecules, thermalMolecules } = initializeMolecules(-15);
    moleculesRef.current = molecules;
    thermalMoleculesRef.current = thermalMolecules;
  }, []);

  const handleTemperatureChange = (delta) => {
    setObjectTemperature((prev) => {
      const minTemperature = COLD_MOLECULE_TEMP;
      const newTemp = Math.max(minTemperature, Math.min(400, prev + delta));

      if (phaseTransitionRef.current) {
        let reducedDelta = delta * 0.1;
        const adjustedTemp = Math.max(
          minTemperature,
          Math.min(400, prev + reducedDelta)
        );

        const currentPhase = phaseTransitionRef.current;
        if (currentPhase === "melting" || currentPhase === "freezing") {
          return MELTING_POINT;
        } else if (
          currentPhase === "vaporizing" ||
          currentPhase === "condensing"
        ) {
          return BOILING_POINT;
        }
        return adjustedTemp;
      }

      const currentTemp = prev;

      if (delta > 0) {
        if (currentTemp < MELTING_POINT && newTemp >= MELTING_POINT) {
          setPhaseTransition("melting");
          setPhaseProgress(0);
          return MELTING_POINT;
        } else if (
          currentTemp < BOILING_POINT &&
          newTemp >= BOILING_POINT &&
          phaseTransitionRef.current !== "melting"
        ) {
          setPhaseTransition("vaporizing");
          setPhaseProgress(0);
          return BOILING_POINT;
        }
      }

      if (delta < 0) {
        if (currentTemp > BOILING_POINT && newTemp <= BOILING_POINT) {
          setPhaseTransition("condensing");
          setPhaseProgress(0);
          return BOILING_POINT;
        } else if (
          currentTemp > MELTING_POINT &&
          newTemp <= MELTING_POINT &&
          phaseTransitionRef.current !== "condensing"
        ) {
          setPhaseTransition("freezing");
          setPhaseProgress(0);
          return MELTING_POINT;
        }
      }

      return newTemp;
    });
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");

    const animate = () => {
      const currentObjectTemp = objectTemperatureRef.current;
      const currentTargetTemp = targetTemperatureRef.current;
      const currentAutoHeating = autoHeatingRef.current;
      const currentAutoCooling = autoCoolingRef.current;
      const currentPhaseTransition = phaseTransitionRef.current;
      const currentPhaseProgress = phaseProgressRef.current;
      const currentShowForces = showForcesRef.current;
      const currentShowShape = showShapeRef.current;

      ctx.clearRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);

      const molecules = moleculesRef.current;
      const thermalMolecules = thermalMoleculesRef.current;

      if (!molecules || !thermalMolecules) return;

      if (currentPhaseTransition) {
        if (
          (currentAutoHeating &&
            (currentPhaseTransition === "melting" ||
              currentPhaseTransition === "vaporizing")) ||
          (currentAutoCooling &&
            (currentPhaseTransition === "freezing" ||
              currentPhaseTransition === "condensing"))
        ) {
          let phaseRate = PHASE_TRANSITION_RATE;
          if (
            currentPhaseTransition === "melting" ||
            currentPhaseTransition === "freezing"
          ) {
            phaseRate = PHASE_TRANSITION_RATE * 3;
          } else if (
            currentPhaseTransition === "vaporizing" ||
            currentPhaseTransition === "condensing"
          ) {
            phaseRate = PHASE_TRANSITION_RATE * 1;
          }

          const newProgress = currentPhaseProgress + phaseRate;

          if (newProgress >= 100) {
            setPhaseTransition(null);
            setPhaseProgress(0);
            shapeHistoryRef.current = []; // 清空形状历史

            setTimeout(() => {
              if (currentAutoHeating) {
                setObjectTemperature((prev) => prev + 1);
              } else if (currentAutoCooling) {
                setObjectTemperature((prev) => prev - 1);
              }
            }, 100);
          } else {
            setPhaseProgress(newProgress);
          }
        }
      }

      if (!currentPhaseTransition) {
        if (!currentAutoHeating && !currentAutoCooling) {
          const tempDiff = currentTargetTemp - currentObjectTemp;
          if (Math.abs(tempDiff) > 1) {
            setObjectTemperature((prev) => prev + tempDiff * 0.02);
          }
        }
      }

      const effectiveTemperature = currentPhaseTransition
        ? currentPhaseTransition === "melting" ||
          currentPhaseTransition === "freezing"
          ? MELTING_POINT
          : BOILING_POINT
        : currentObjectTemp;

      molecules.forEach((mol) => {
        mol.calculateForces(
          molecules,
          effectiveTemperature,
          currentPhaseTransition,
          currentPhaseProgress
        );
        mol.checkThermalCollision(thermalMolecules, handleTemperatureChange);
        mol.update(
          effectiveTemperature,
          currentPhaseTransition,
          currentPhaseProgress
        );
      });

      thermalMolecules.forEach((thermal) => {
        thermal.update(
          effectiveTemperature,
          currentPhaseTransition,
          currentPhaseProgress
        );
      });

      Molecule.manageThermalMolecules(
        thermalMolecules,
        currentAutoHeating,
        currentAutoCooling
      );

      const newState = determineState(effectiveTemperature);
      setCurrentState(newState);

      if (currentShowShape) {
        const molecularShapes = calculateMolecularShape(
          molecules,
          newState,
          currentPhaseTransition
        );
        if (molecularShapes) {
          if (newState === "固体") {
            drawMolecularShapes(ctx, molecularShapes, newState);
          } else if (
            newState === "液体" ||
            (currentPhaseTransition &&
              (currentPhaseTransition === "vaporizing" ||
                currentPhaseTransition === "condensing"))
          ) {
            drawMolecularShapes(ctx, molecularShapes, "液体");
          }
        }
      }

      if (currentShowForces) {
        molecules.forEach((mol) => {
          if (mol.forces && mol.forces.length > 0) {
            mol.forces.forEach((force) => {
              const forceMagnitude = Math.max(force.magnitude, 0.01);
              const alpha = Math.min(forceMagnitude * 2, 0.8);

              if (force.type === "attraction") {
                ctx.strokeStyle = `rgba(0, 100, 255, ${alpha})`;
                ctx.fillStyle = `rgba(0, 100, 255, ${alpha})`;
              } else if (force.type === "surface_tension") {
                ctx.strokeStyle = `rgba(0, 200, 100, ${alpha})`;
                ctx.fillStyle = `rgba(0, 200, 100, ${alpha})`;
              } else {
                ctx.strokeStyle = `rgba(255, 50, 50, ${alpha})`;
                ctx.fillStyle = `rgba(255, 50, 50, ${alpha})`;
              }

              ctx.lineWidth = 2;
              const forceLength = Math.sqrt(
                force.forceX * force.forceX + force.forceY * force.forceY
              );
              if (forceLength > 0) {
                const normalizedFx = force.forceX / forceLength;
                const normalizedFy = force.forceY / forceLength;
                const arrowLength = 25;
                const endX = mol.x + normalizedFx * arrowLength;
                const endY = mol.y + normalizedFy * arrowLength;

                ctx.beginPath();
                ctx.moveTo(mol.x, mol.y);
                ctx.lineTo(endX, endY);
                ctx.stroke();

                const angle = Math.atan2(normalizedFy, normalizedFx);
                const arrowHeadLength = 8;
                const arrowHeadAngle = Math.PI / 6;

                ctx.beginPath();
                ctx.moveTo(endX, endY);
                ctx.lineTo(
                  endX - arrowHeadLength * Math.cos(angle - arrowHeadAngle),
                  endY - arrowHeadLength * Math.sin(angle - arrowHeadAngle)
                );
                ctx.moveTo(endX, endY);
                ctx.lineTo(
                  endX - arrowHeadLength * Math.cos(angle + arrowHeadAngle),
                  endY - arrowHeadLength * Math.sin(angle + arrowHeadAngle)
                );
                ctx.stroke();
              }
            });
          }
        });
      }

      // 使用新的连接线显示逻辑
      if (
        shouldShowMolecularBonds(
          newState,
          currentPhaseTransition,
          currentPhaseProgress
        ) &&
        !currentShowForces
      ) {
        // 根据物态变化进度调整连接线的透明度和密度
        let connectionOpacity = 1.0;
        let maxDistance = newState === "固体" ? 50 : 70;

        if (currentPhaseTransition === "vaporizing") {
          // 汽化过程中，连接线逐渐变淡，距离逐渐增大
          const fadeProgress = Math.max(0, (currentPhaseProgress - 30) / 40); // 30%-70%之间逐渐消失
          connectionOpacity = Math.max(0.1, 1.0 - fadeProgress);
          maxDistance = 70 + fadeProgress * 30; // 距离逐渐增大
        } else if (currentPhaseTransition === "condensing") {
          // 液化过程中，连接线逐渐显现，距离逐渐减小
          const appearProgress = Math.max(0, (currentPhaseProgress - 30) / 40); // 30%之后逐渐显现
          connectionOpacity = Math.min(1.0, appearProgress);
          maxDistance = 100 - appearProgress * 30; // 距离逐渐减小
        }

        ctx.strokeStyle =
          newState === "固体"
            ? `rgba(150, 150, 150, ${0.4 * connectionOpacity})`
            : `rgba(100, 100, 100, ${0.2 * connectionOpacity})`;
        ctx.lineWidth = newState === "固体" ? 2 : 1;

        molecules.forEach((mol1, i) => {
          molecules.forEach((mol2, j) => {
            if (i >= j) return;
            const dx = mol2.x - mol1.x;
            const dy = mol2.y - mol1.y;
            const distance = Math.sqrt(dx * dx + dy * dy);

            if (distance < maxDistance) {
              ctx.beginPath();
              ctx.moveTo(mol1.x, mol1.y);
              ctx.lineTo(mol2.x, mol2.y);
              ctx.stroke();
            }
          });
        });
      }

      molecules.forEach((mol, index) => {
        const speed = Math.sqrt(mol.vx * mol.vx + mol.vy * mol.vy);
        const intensity = Math.min(speed * 10, 255);

        let baseColor = `rgb(${50 + intensity}, ${150 - intensity / 2}, 255)`;

        if (currentPhaseTransition) {
          const moleculeIndex = (mol.x * 0.1 + mol.y * 0.1) % 1000;
          const progressThreshold = (moleculeIndex % 1000) / 1000;
          const smoothProgress =
            currentPhaseProgress / 100 +
            Math.sin(Date.now() * 0.001 + moleculeIndex) * 0.03;
          const transitionRatio = Math.max(
            0,
            Math.min(1, (smoothProgress - progressThreshold + 0.1) / 0.2)
          );

          if (currentPhaseTransition === "melting") {
            const solidColor = [50 + intensity / 2, 120 - intensity / 4, 255];
            const liquidColor = [100 + intensity, 180 - intensity / 3, 255];
            const r =
              solidColor[0] +
              (liquidColor[0] - solidColor[0]) * transitionRatio;
            const g =
              solidColor[1] +
              (liquidColor[1] - solidColor[1]) * transitionRatio;
            const b =
              solidColor[2] +
              (liquidColor[2] - solidColor[2]) * transitionRatio;
            baseColor = `rgb(${Math.round(r)}, ${Math.round(g)}, ${Math.round(
              b
            )})`;
          } else if (currentPhaseTransition === "vaporizing") {
            const liquidColor = [100 + intensity, 180 - intensity / 3, 255];
            const gasColor = [150 + intensity, 200 - intensity / 4, 200];
            const r =
              liquidColor[0] + (gasColor[0] - liquidColor[0]) * transitionRatio;
            const g =
              liquidColor[1] + (gasColor[1] - liquidColor[1]) * transitionRatio;
            const b =
              liquidColor[2] + (gasColor[2] - liquidColor[2]) * transitionRatio;
            baseColor = `rgb(${Math.round(r)}, ${Math.round(g)}, ${Math.round(
              b
            )})`;
          } else if (currentPhaseTransition === "freezing") {
            const liquidColor = [100 + intensity, 180 - intensity / 3, 255];
            const solidColor = [50 + intensity / 2, 120 - intensity / 4, 255];
            const r =
              liquidColor[0] +
              (solidColor[0] - liquidColor[0]) * transitionRatio;
            const g =
              liquidColor[1] +
              (solidColor[1] - liquidColor[1]) * transitionRatio;
            const b =
              liquidColor[2] +
              (solidColor[2] - liquidColor[2]) * transitionRatio;
            baseColor = `rgb(${Math.round(r)}, ${Math.round(g)}, ${Math.round(
              b
            )})`;
          } else if (currentPhaseTransition === "condensing") {
            const gasColor = [150 + intensity, 200 - intensity / 4, 200];
            const liquidColor = [100 + intensity, 180 - intensity / 3, 255];
            const r =
              gasColor[0] + (liquidColor[0] - gasColor[0]) * transitionRatio;
            const g =
              gasColor[1] + (liquidColor[1] - gasColor[1]) * transitionRatio;
            const b =
              gasColor[2] + (liquidColor[2] - gasColor[2]) * transitionRatio;
            baseColor = `rgb(${Math.round(r)}, ${Math.round(g)}, ${Math.round(
              b
            )})`;
          }
        }

        ctx.beginPath();
        ctx.arc(mol.x, mol.y, mol.radius, 0, Math.PI * 2);
        ctx.fillStyle = baseColor;
        ctx.fill();
        ctx.strokeStyle = "#333";
        ctx.lineWidth = 1;
        ctx.stroke();
      });

      thermalMolecules.forEach((thermal) => {
        ctx.beginPath();
        ctx.arc(thermal.x, thermal.y, thermal.radius, 0, Math.PI * 2);

        if (thermal.isCold) {
          ctx.fillStyle = "#4488ff";
          ctx.fill();
          const gradient = ctx.createRadialGradient(
            thermal.x,
            thermal.y,
            0,
            thermal.x,
            thermal.y,
            12
          );
          gradient.addColorStop(0, "rgba(100, 150, 255, 0.4)");
          gradient.addColorStop(1, "rgba(100, 150, 255, 0)");
          ctx.fillStyle = gradient;
          ctx.fill();
        } else {
          ctx.fillStyle = "#ff4444";
          ctx.fill();
          const gradient = ctx.createRadialGradient(
            thermal.x,
            thermal.y,
            0,
            thermal.x,
            thermal.y,
            15
          );
          gradient.addColorStop(0, "rgba(255, 100, 100, 0.6)");
          gradient.addColorStop(1, "rgba(255, 100, 100, 0)");
          ctx.fillStyle = gradient;
          ctx.fill();
        }
      });

      animationRef.current = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, []);

  const handleTargetTempChange = (newTemp) => {
    setTargetTemperature(newTemp);
    if (!autoHeating && !autoCooling) {
      if (Math.abs(newTemp - objectTemperature) > 50) {
        const { molecules, thermalMolecules } = initializeMolecules(newTemp);
        moleculesRef.current = molecules;
        thermalMoleculesRef.current = thermalMolecules;
        setObjectTemperature(newTemp);
        setPhaseTransition(null);
        setPhaseProgress(0);
        shapeHistoryRef.current = []; // 清空形状历史
      }
    }
  };

  const startAutoHeating = () => {
    setAutoHeating(true);
    setAutoCooling(false);
  };

  const startAutoCooling = () => {
    setAutoCooling(true);
    setAutoHeating(false);
  };

  const stopAuto = () => {
    setAutoHeating(false);
    setAutoCooling(false);
    setPhaseTransition(null);
    setPhaseProgress(0);
  };

  const resetSimulation = () => {
    setAutoHeating(false);
    setAutoCooling(false);
    setObjectTemperature(-15);
    setTargetTemperature(-15);
    setPhaseTransition(null);
    setPhaseProgress(0);
    shapeHistoryRef.current = []; // 清空形状历史
    const { molecules, thermalMolecules } = initializeMolecules(-15);
    moleculesRef.current = molecules;
    thermalMoleculesRef.current = thermalMolecules;
  };

  const getStateColor = (state) => {
    switch (state) {
      case "固体":
        return "#4CAF50";
      case "液体":
        return "#2196F3";
      case "气体":
        return "#FF9800";
      default:
        return "#666";
    }
  };

  const getStateDescription = (state) => {
    switch (state) {
      case "固体":
        return "分子排列紧密规整，只能在固定位置振动";
      case "液体":
        return "分子间距适中，可以流动但仍有一定约束";
      case "气体":
        return "分子间距很大，运动自由，填满整个容器";
      default:
        return "";
    }
  };

  return (
    <div className="w-full max-w-6xl mx-auto p-6 bg-gray-50 rounded-lg">
      <h1 className="text-3xl font-bold text-center mb-6 text-gray-800">
        物态变化模拟-By陈硕老师
      </h1>

      <div className="mb-6 text-center">
        <div
          className="inline-block p-4 rounded-lg"
          style={{ backgroundColor: getStateColor(currentState) + "20" }}
        >
          <h2
            className="text-2xl font-bold mb-2"
            style={{ color: getStateColor(currentState) }}
          >
            当前状态：{currentState}
          </h2>
          <p className="text-gray-600">{getStateDescription(currentState)}</p>

          <div className="mt-3 flex justify-center gap-4 text-sm">
            <span className="bg-white px-3 py-1 rounded">
              物体温度:{" "}
              {phaseTransition
                ? (phaseTransition === "melting" ||
                  phaseTransition === "freezing"
                    ? MELTING_POINT
                    : BOILING_POINT) + "°C (恒定)"
                : Math.round(objectTemperature) + "°C"}
            </span>
            <span className="bg-red-100 px-3 py-1 rounded">
              热分子温度: {HOT_MOLECULE_TEMP}°C
            </span>
            <span className="bg-blue-100 px-3 py-1 rounded">
              冷分子温度: {COLD_MOLECULE_TEMP}°C
            </span>
          </div>

          {phaseTransition && (
            <div className="mt-3 bg-yellow-100 p-3 rounded-lg">
              <div className="text-lg font-semibold text-yellow-800 mb-2">
                🔄{" "}
                {getPhaseTransitionDescription(phaseTransition, phaseProgress)}
              </div>
              <div className="w-full bg-yellow-200 rounded-full h-3">
                <div
                  className="bg-yellow-500 h-3 rounded-full transition-all duration-300"
                  style={{ width: `${phaseProgress}%` }}
                ></div>
              </div>
              <p className="text-xs text-yellow-700 mt-1">
                温度锁定在
                {phaseTransition === "melting" || phaseTransition === "freezing"
                  ? MELTING_POINT
                  : BOILING_POINT}
                °C恒定不变，能量用于物态变化
                {phaseTransition === "melting" || phaseTransition === "freezing"
                  ? "（约30秒）"
                  : "（约3分钟）"}
              </p>
            </div>
          )}
        </div>
      </div>

      <div className="flex flex-col lg:flex-row gap-6">
        <div className="flex-1">
          <canvas
            ref={canvasRef}
            width={CANVAS_WIDTH}
            height={CANVAS_HEIGHT}
            className="border-2 border-gray-300 rounded-lg bg-white shadow-lg"
            style={{ maxWidth: "100%", height: "auto" }}
          />
        </div>

        <div className="lg:w-80 space-y-6">
          <div className="bg-white p-6 rounded-lg shadow-md">
            <h3 className="text-xl font-semibold mb-4">温度控制</h3>

            <div className="space-y-3 mb-4">
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={startAutoHeating}
                  className={`px-4 py-2 rounded font-medium transition-colors ${
                    autoHeating
                      ? "bg-red-500 text-white"
                      : "bg-gray-200 text-gray-700 hover:bg-red-200"
                  }`}
                >
                  🔥 自动加热
                </button>
                <button
                  onClick={startAutoCooling}
                  className={`px-4 py-2 rounded font-medium transition-colors ${
                    autoCooling
                      ? "bg-blue-500 text-white"
                      : "bg-gray-200 text-gray-700 hover:bg-blue-200"
                  }`}
                >
                  ❄️ 自动冷却
                </button>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={stopAuto}
                  className="px-4 py-2 bg-yellow-500 text-white rounded hover:bg-yellow-600 font-medium"
                >
                  ⏸️ 停止自动
                </button>
                <button
                  onClick={resetSimulation}
                  className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 font-medium"
                >
                  🔄 重置
                </button>
              </div>
              {(autoHeating || autoCooling) && (
                <div className="text-xs text-gray-600 bg-yellow-50 p-2 rounded">
                  <p>
                    {autoHeating
                      ? "🔥 热分子正在自动加热物体..."
                      : "❄️ 冷分子正在自动冷却物体..."}
                  </p>
                  <p className="mt-1 text-gray-500">
                    ⏱️ 温度变化通过分子碰撞实现，符合真实热传递原理
                  </p>
                </div>
              )}
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  目标温度: {targetTemperature}°C{" "}
                  {!autoHeating && !autoCooling
                    ? "(手动模式)"
                    : "(当前被自动模式覆盖)"}
                </label>
                <input
                  type="range"
                  min="-40"
                  max="200"
                  value={targetTemperature}
                  onChange={(e) =>
                    handleTargetTempChange(parseInt(e.target.value))
                  }
                  disabled={autoHeating || autoCooling}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer disabled:opacity-50"
                />
              </div>

              <div className="grid grid-cols-3 gap-2">
                <button
                  onClick={() => handleTargetTempChange(-20)}
                  disabled={autoHeating || autoCooling}
                  className="px-3 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 text-sm disabled:opacity-50"
                >
                  冰(-20°C)
                </button>
                <button
                  onClick={() => handleTargetTempChange(25)}
                  disabled={autoHeating || autoCooling}
                  className="px-3 py-2 bg-green-500 text-white rounded hover:bg-green-600 text-sm disabled:opacity-50"
                >
                  水(25°C)
                </button>
                <button
                  onClick={() => handleTargetTempChange(150)}
                  disabled={autoHeating || autoCooling}
                  className="px-3 py-2 bg-orange-500 text-white rounded hover:bg-orange-600 text-sm disabled:opacity-50"
                >
                  蒸汽(150°C)
                </button>
              </div>

              <div className="space-y-3">
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={showForces}
                    onChange={(e) => setShowForces(e.target.checked)}
                    className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <span className="text-sm font-medium text-gray-700">
                    显示分子间作用力
                  </span>
                </label>

                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={showShape}
                    onChange={(e) => setShowShape(e.target.checked)}
                    className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <span className="text-sm font-medium text-gray-700">
                    显示物体形状
                  </span>
                </label>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-md">
            <h3 className="text-xl font-semibold mb-4">分子运动原理</h3>
            <div className="space-y-2 text-sm">
              <div className="p-3 bg-red-50 rounded">
                <p className="font-semibold text-red-800">🔥 热分子的运动</p>
                <p className="text-red-600 text-xs mt-1">
                  • 高温分子运动速度快，携带更多动能
                </p>
                <p className="text-red-600 text-xs">
                  • 与物体分子碰撞，传递热量和动能
                </p>
                <p className="text-red-600 text-xs">
                  • 温度越高，分子运动越剧烈
                </p>
              </div>
              <div className="p-3 bg-blue-50 rounded">
                <p className="font-semibold text-blue-800">❄️ 冷分子的运动</p>
                <p className="text-blue-600 text-xs mt-1">
                  • 低温分子运动速度慢，动能较小
                </p>
                <p className="text-blue-600 text-xs">
                  • 与物体分子碰撞，吸收热量和动能
                </p>
                <p className="text-blue-600 text-xs">• 帮助物体分子减速降温</p>
              </div>
              {/* <div className="p-3 bg-green-50 rounded">
                <p className="font-semibold text-green-800">💧 液体的形成</p>
                <p className="text-green-600 text-xs mt-1">
                  • 分子间距适中，既能流动又相互吸引
                </p>
                <p className="text-green-600 text-xs">
                  • 表面张力让液滴保持相对稳定的形状
                </p>
                <p className="text-green-600 text-xs">
                  • 温度改变时，液滴会合并或分离
                </p>
              </div> */}
              <div className="p-3 bg-purple-50 rounded">
                <p className="font-semibold text-purple-800">
                  🌡️ 温度与分子运动
                </p>
                <p className="text-purple-600 text-xs mt-1">
                  • 温度反映分子平均动能的大小
                </p>
                <p className="text-purple-600 text-xs">
                  • 固态时分子振动，液态时分子流动
                </p>
                <p className="text-purple-600 text-xs">
                  • 气态时分子自由运动，填满空间
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-md">
            <h3 className="text-xl font-semibold mb-4">观察重点</h3>
            <div className="text-sm text-gray-600 space-y-2">
              <p className="font-semibold text-gray-800">
                🔍 观察内能的两个层面：：
              </p>
              <div className="pl-4 space-y-2">
                <p>
                  <strong>1. 分子动能：</strong>
                  观察不同温度下分子运动速度的差异
                </p>
                <p>
                  <strong>2. 分子势能：</strong>
                  注意物态变化时分子间距和排列的改变
                </p>
              </div>
              <div className="mt-3 p-3 bg-yellow-50 rounded">
                <p className="font-semibold text-yellow-800">
                  ⚡ 物态变化时的能量转换
                </p>
                <p className="text-yellow-700 text-xs mt-1">
                  • 物态变化时温度保持不变，能量用于改变分子排列
                </p>
                <p className="text-yellow-700 text-xs">
                  • 熔化/汽化：能量转化为分子势能，克服引力
                </p>
                <p className="text-yellow-700 text-xs">
                  • 凝固/液化：分子势能释放，形成更紧密结构
                </p>
              </div>
              {/* <p className="mt-3 p-2 bg-cyan-50 rounded text-cyan-800">
                <strong>🧊 建议实验：</strong>从固态开始自动加热，仔细观察： ①
                固体熔化时的分子运动变化 ② 液体汽化时连接线的消失过程 ③
                整个物态变化过程中温度的变化规律
              </p> */}
            </div>
          </div>
        </div>
      </div>

      <footer className="mt-8 w-full text-center text-sm text-gray-500">
        © {new Date().getFullYear()} 孙维刚教育研究院-陈硕老师 保留所有权利。
      </footer>
    </div>
  );
};

export default MolecularStatesSimulation;
